import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { fadeInUp, staggerContainer, viewportConfig } from '@/lib/animations';
import { generalFAQs, governmentFAQs, businessFAQs, type FAQItem } from '@/data/faqData';
import { scrollToHash } from '@/lib/scroll';

type TabType = 'general' | 'government' | 'business';

const tabs: { id: TabType; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'government', label: 'Government Clients' },
  { id: 'business', label: 'Business Clients' },
];

function FAQAccordion({ items }: { items: FAQItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className={`border rounded-xl overflow-hidden transition-all duration-300 ${
            openIndex === index 
              ? 'border-orange bg-orange/5 border-l-4 border-l-orange' 
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          {/* Question */}
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full p-5 flex items-center justify-between text-left hover:bg-gray-50/50 transition-colors"
          >
            <span className="font-semibold text-navy-deep pr-4">{item.question}</span>
            <motion.div
              animate={{ rotate: openIndex === index ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="flex-shrink-0"
            >
              <ChevronDown className="w-5 h-5 text-gray-400" />
            </motion.div>
          </button>
          
          {/* Answer */}
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="px-5 pb-5 text-muted-foreground leading-relaxed text-sm">
                  {item.answer}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>
  );
}

export function FAQSection() {
  const [activeTab, setActiveTab] = useState<TabType>('general');

  const getFAQs = () => {
    switch (activeTab) {
      case 'government':
        return governmentFAQs;
      case 'business':
        return businessFAQs;
      default:
        return generalFAQs;
    }
  };

  const scrollToContact = () => {
    scrollToHash('#contact');
  };

  return (
    <section id="faq" className="py-12 sm:py-14 md:py-16 lg:py-20 bg-gray-50">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportConfig}
        variants={staggerContainer}
        className="container mx-auto px-5 sm:px-6"
      >
        {/* Section Header */}
        <motion.div variants={fadeInUp} className="text-center mb-10 md:mb-14">
          <span className="text-teal font-semibold text-xs sm:text-sm tracking-widest uppercase mb-2 sm:mb-3 block">
            Frequently Asked Questions
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-navy-deep tracking-tight mb-4">
            Questions? <span className="text-orange">We've Got Answers.</span>
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
            Everything you need to know about working with Yaw CTV.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div variants={fadeInUp} className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-full p-1.5 shadow-lg">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 sm:px-6 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-navy-deep text-white shadow-md'
                    : 'text-gray-600 hover:text-navy-deep'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div variants={fadeInUp} className="max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <FAQAccordion items={getFAQs()} />
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div variants={fadeInUp} className="text-center mt-12">
          <p className="text-muted-foreground mb-4">Still have questions?</p>
          <button 
            onClick={scrollToContact}
            className="inline-flex items-center text-orange hover:text-orange-hover font-semibold transition-colors"
          >
            Contact our team
            <ArrowRight className="ml-2 w-4 h-4" />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
